import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, Mail } from "lucide-react";
import CaprocureLogo from "@/components/CaprocureLogo";

// Kick off a password reset. The server ALWAYS responds identically for known
// and unknown emails — so the UI shows the same neutral confirmation once the
// request has been sent, regardless of whether a matching account exists.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (e) {
      // Rate-limit hits are surfaced verbatim; every other error is folded
      // into the neutral "check your inbox" screen to preserve non-enumeration.
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if ((e.response?.status || 0) === 429) {
        setErr(detail);
      } else {
        setDone(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full p-10 border-slate-200">
        <Link to="/" className="mb-6 inline-block">
          <CaprocureLogo size="md" />
        </Link>

        {done ? (
          <div className="text-center py-6" data-testid="forgot-success">
            <div className="w-12 h-12 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center mx-auto">
              <Mail className="w-6 h-6 text-sky-600" />
            </div>
            <div className="eyebrow mt-4">Check your inbox</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540] mt-1">Reset link sent</h2>
            <p className="text-sm text-slate-500 mt-2">
              If an account exists for that email, a reset link is on its way. The link expires in one hour and can only be used once.
            </p>
            <div className="mt-6 text-sm">
              Back to <Link to="/login" className="text-[#1D4ED8] font-medium">sign in</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="eyebrow mb-2">Password reset</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540]">Forgot your password?</h2>
            <p className="text-sm text-slate-500 mt-2">
              Enter the email you registered with. We'll send you a one-time link to choose a new password.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email *</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="forgot-email-input"
                />
              </div>
              {err && <div className="text-sm text-rose-600" data-testid="forgot-error">{err}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] hover:bg-[#061A2E]" data-testid="forgot-submit-btn">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-sm text-center text-slate-600">
                Back to <Link to="/login" className="text-[#1D4ED8] font-medium">sign in</Link>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
